/**
 * @lodex-pro/web-admin — entry point (Phase 25 / Task 25.1, DEC-111).
 *
 * Bootstraps Amplify v6 BEFORE the React tree mounts so
 * `cognitoUserPoolsTokenProvider.setKeyValueStorage(...)` is wired before any
 * call to `signIn`, `confirmSignIn`, `fetchAuthSession`, etc.
 *
 * Tenant type literal: `platform` (Paelode Systems internal staff only — the
 * fourth Cognito pool). Pools come from `VITE_COGNITO_POOL_CONFIG_JSON` (4 entries
 * in CDK iteration order — hauler, operator, disposal, platform).
 *
 * Provider nesting mirrors web-dispatch: QueryProvider → ApiOptsProvider → App,
 * with the dark-theme stylesheet imported first.
 *
 * Spec: CONTEXT D-03, D-05, D-07; RESEARCH § Pattern 4; gsd phase_25 Task 25.1.
 */

// CSS entry must be imported first (Tailwind v4 + shared dark tokens).
import './styles.css';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { bootstrapWebAmplify, QueryProvider, ApiOptsProvider } from '@lodex-pro/web-shared';
import type { PoolConfigEntry } from '@lodex-pro/shared/auth/amplify-config';

import { App } from './App';

const POOLS_RAW = (import.meta.env['VITE_COGNITO_POOL_CONFIG_JSON'] as string | undefined) ?? '[]';
const API_BASE_URL =
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? 'http://localhost:3000';
const pools = JSON.parse(POOLS_RAW) as ReadonlyArray<PoolConfigEntry>;

bootstrapWebAmplify({
  pools,
  tenantType: 'platform',
  apiBaseUrl: API_BASE_URL,
});

const rootElement = document.getElementById('root');
if (rootElement === null) {
  throw new Error('Root element #root not found in index.html');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryProvider>
      <ApiOptsProvider opts={{ baseUrl: API_BASE_URL }}>
        <App />
      </ApiOptsProvider>
    </QueryProvider>
  </React.StrictMode>,
);
